import React from 'react';
import { Package, Lightbulb, MessageCircle, ClipboardList } from 'lucide-react';

/**
 * Presentation metadata for the 4 use cases, keyed by the backend use-case key.
 *
 * In the APP, a use case always renders with the SAME chip — neutral grey with a dark
 * icon (teal-tinted when selected) — on the New study cards, in My studies and in the
 * chat header. `tileBg`/`iconColor` are the older per-use-case pastels, kept only for
 * the public landing page, which still uses them.
 */
export interface UseCaseMeta {
  title: string;
  icon: React.ReactNode;
  tileBg: string; // hex for the icon tile background (summary tiles)
  iconColor: string; // hex for the icon stroke (summary tiles)
  desc: string;
  /** What the user must bring to run this study — shown as the card's pill. */
  needs: string;
  /** Free-entitlement sub-line. Every string is framed as part of the SINGLE
   *  global free study (there is one free study total, not one per card). */
  limit: string;
  /** Paid framing shown in the pay-per-Twin flow / once the free study is
   *  used — never mentions a free inclusion. */
  paidLimit: string;
  limitColor: string; // hex matching the icon, for the "included" line
}

const ICON = 'h-[22px] w-[22px]';

// Icons carry NO inline colour — they inherit `currentColor` from the tile, so
// the card can tint them (teal when selected, neutral otherwise) while the
// summary tiles keep their original categorical colour.
export const USE_CASE_META: Record<string, UseCaseMeta> = {
  packaging: {
    title: 'Packaging test',
    icon: <Package className={ICON} />,
    tileBg: '#EEEDFE',
    iconColor: '#534AB7',
    desc: 'See how your pack reads on shelf — clarity, appeal, claims, purchase intent.',
    needs: 'Needs: front / competitor images',
    limit: 'Your 1 free study: tests 1 design',
    paidLimit: '1 design per study · billed per Twin',
    limitColor: '#534AB7',
  },
  concept: {
    title: 'Concept test',
    icon: <Lightbulb className={ICON} />,
    tileBg: '#FAEEDA',
    iconColor: '#854F0B',
    desc: 'Validate a new product or claim before you build it — appeal, believability, gaps.',
    needs: 'Needs: concept cards or written concept',
    limit: 'Your 1 free study: tests 1 concept',
    paidLimit: '1 concept per study · billed per Twin',
    limitColor: '#854F0B',
  },
  chat: {
    title: 'Chat with Twins',
    icon: <MessageCircle className={ICON} />,
    tileBg: '#E8FCFA',
    iconColor: '#07E5D1',
    desc: 'Run a focus group with your target customer segment and understand the why’s behind their decisions.',
    needs: 'Needs: your questions (live)',
    limit: 'Your 1 free study: 1 chat with 3 queries',
    paidLimit: '3 queries per study · billed per Twin',
    limitColor: '#0B7C74',
  },
  survey: {
    title: 'Survey',
    icon: <ClipboardList className={ICON} />,
    tileBg: '#E1F5EE',
    iconColor: '#0F6E56',
    desc: 'Run quant + qual at once — rate, rank and explain, across hundreds of Twins.',
    needs: 'Needs: question set',
    limit: 'Your 1 free study: 1 full survey run',
    paidLimit: 'Full survey run · billed per Twin',
    limitColor: '#0F6E56',
  },
};

export const USE_CASE_ORDER = ['packaging', 'concept', 'chat', 'survey'] as const;

// Selected-card accents: a light cyan wash + its readable ink. Applied ONLY to
// the icon tile and the entitlement pill — never to the card body.
const SELECTED_TINT = '#E8FCFA';
const SELECTED_INK = '#0B7C74';

// Unselected cards use a neutral chip so the grid reads as one calm system and
// the cyan selection is the only colour that pops.
const NEUTRAL_TILE = '#f2f2f0';
const NEUTRAL_ICON = '#3f3f3f';

/**
 * THE display name for a use case. Everything user-facing must go through this —
 * screens were rendering the raw backend key ("chat", "concept") with a CSS
 * `capitalize`, and a second hand-maintained label map had drifted, so the same use
 * case appeared as "Chat", "Chat with twins" and "Chat with your Twins".
 */
export const labelForUseCase = (key: string): string => USE_CASE_META[key]?.title ?? key;

/**
 * SHORT name, for dense lists (My studies). The full card titles ("Chat with twins",
 * "Concept test") are too long once they sit next to an audience, a twin count and a
 * price on one line — and a row titled "Chat with twins" whose subtitle also began
 * "Chat with twins ·" read as a stutter.
 */
const SHORT_USE_CASE_LABELS: Record<string, string> = {
  chat: 'Chat',
  concept: 'Concept',
  survey: 'Survey',
  packaging: 'Packaging',
};
export const shortLabelForUseCase = (key: string): string =>
  SHORT_USE_CASE_LABELS[key] ?? labelForUseCase(key);

export const useCaseIcon = (key: string) => USE_CASE_META[key]?.icon ?? null;
export const useCaseIncluded = (key: string, freeAvailable = true) => {
  const m = USE_CASE_META[key];
  if (!m) return '';
  return freeAvailable ? m.limit : m.paidLimit;
};

/** The 2×2 use-case card from the mockups (icon tile + title + desc + limit).
 *  `freeAvailable` picks the entitlement sub-line: free-study framing while
 *  the single free study is still available, paid framing otherwise. */
export const UseCaseCard: React.FC<{
  useCaseKey: string;
  selected: boolean;
  onClick: () => void;
  freeAvailable?: boolean;
}> = ({ useCaseKey, selected, onClick, freeAvailable = true }) => {
  const m = USE_CASE_META[useCaseKey];
  if (!m) return null;
  return (
    <div
      role="button"
      tabIndex={0}
      aria-pressed={selected}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      className={[
        // The card stays WHITE when selected — only the border turns cyan and the
        // icon tile / entitlement pill pick up the teal tint. A fully tinted card
        // fights with the grey page and loses the raised, elegant look.
        'flex cursor-pointer gap-3.5 rounded-xl border-[1.5px] bg-white p-5 transition-colors',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
        selected ? 'border-primary' : 'border-black/10 hover:border-black/20',
      ].join(' ')}
    >
      <div
        className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-lg transition-colors"
        style={
          selected
            ? { background: SELECTED_TINT, color: SELECTED_INK }
            : { background: NEUTRAL_TILE, color: NEUTRAL_ICON }
        }
      >
        {m.icon}
      </div>
      <div className="flex flex-col items-start gap-2">
        <div>
          <div className="text-[15px] font-medium text-[#111]">{m.title}</div>
          <div className="mt-1 text-[13px] leading-normal text-[#6b6b6b]">{m.desc}</div>
        </div>
        <span
          className="mt-auto rounded-md px-2 py-1 text-[11px] transition-colors"
          style={
            selected
              ? { background: SELECTED_TINT, color: SELECTED_INK }
              : { background: NEUTRAL_TILE, color: '#6b6b6b' }
          }
        >
          {m.needs}
        </span>
        {/* Free framing lives in the page subheading ("Your first study is free"),
            so the card only needs the entitlement line once the free study is
            gone — otherwise the paid flow would show no pricing cue at all. */}
        {!freeAvailable && (
          <span className="text-[11px]" style={{ color: m.limitColor }}>
            {m.paidLimit}
          </span>
        )}
      </div>
    </div>
  );
};

/** Loading placeholder matching the UseCaseCard shape (avoids a sudden pop-in). */
export const UseCaseCardSkeleton: React.FC = () => (
  <div className="flex gap-3.5 rounded-xl border border-black/10 bg-white p-5">
    <div className="h-11 w-11 flex-shrink-0 animate-pulse rounded-lg bg-black/[0.06]" />
    <div className="w-full space-y-2">
      <div className="h-3.5 w-1/2 animate-pulse rounded bg-black/[0.06]" />
      <div className="h-3 w-full animate-pulse rounded bg-black/[0.06]" />
      <div className="h-3 w-4/5 animate-pulse rounded bg-black/[0.06]" />
      <div className="h-5 w-2/5 animate-pulse rounded-md bg-black/[0.06]" />
    </div>
  </div>
);

/** Small icon tile for lists and summary bars (My studies, chat header). */
export const UseCaseTile: React.FC<{ useCaseKey: string; size?: number }> = ({
  useCaseKey,
  size = 32,
}) => {
  const m = USE_CASE_META[useCaseKey];
  if (!m) return null;
  return (
    <div
      // SAME chip as the use-case cards (neutral grey + dark icon). It used to use the
      // per-use-case pastel `tileBg`, so the same study rendered one way on the New
      // study cards and another in My studies / chat.
      className="flex flex-shrink-0 items-center justify-center rounded-lg border border-black/[0.06]"
      style={{ background: NEUTRAL_TILE, color: NEUTRAL_ICON, width: size, height: size }}
    >
      {React.isValidElement(m.icon)
        ? React.cloneElement(m.icon as React.ReactElement, {
            className: 'h-[17px] w-[17px]',
          })
        : m.icon}
    </div>
  );
};
